import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  BOT_AVATAR_COLORS,
  BotAvatar,
  BotShapePreview,
  Button,
  DEFAULT_BOT_AVATAR_COLOR,
  Dialog,
  DialogContent,
  DialogTitle,
  defaultBotAvatarValue,
  parseBotAvatar,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@rakazo/ui-web";
import { ArrowLeft, ImagePlus, Pencil, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { appMarkUrl } from "../../lib/branding";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";
import { AvatarPhotoCrop } from "./avatar-photo-crop";

export interface AvatarStudioPopoverProps {
  value: string;
  identity?: string;
  status?: string;
  size?: number;
  onChange: (value: string) => void;
  disabled?: boolean;
}
export function AvatarStudioPopover({
  value,
  identity,
  status,
  size = 72,
  onChange,
  disabled = false,
}: AvatarStudioPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [tab, setTab] = useState("bot");
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const generation = useRef<AbortController | null>(null);
  const parsed = parseBotAvatar(draft);
  const currentColor = parsed.color || DEFAULT_BOT_AVATAR_COLOR;
  const isDefault = !parsed.isImage && !parsed.isBrand && parsed.shapeIndex === undefined;
  useEffect(() => {
    if (!open) return;
    let active = true;
    void rpc.bots
      .avatarGeneration()
      .then((result) => {
        if (active) setAvailable(result.available);
      })
      .catch(() => {
        if (active) setAvailable(false);
      });
    return () => {
      active = false;
      generation.current?.abort();
    };
  }, [open]);
  function chooseFile(file: File) {
    setError("");
    if (!file.type.startsWith("image/") || file.size > 25 * 1024 * 1024) {
      setError(t`Choose an image under 25 MB.`);
      return;
    }
    setPhoto(file);
  }
  async function generate() {
    if (!prompt.trim() || generating) return;
    const controller = new AbortController();
    generation.current = controller;
    setGenerating(true);
    setError("");
    try {
      const result = await rpc.bots.generateAvatar(
        { prompt: prompt.trim() },
        { signal: controller.signal },
      );
      if (!controller.signal.aborted) setDraft(result.color);
    } catch (err) {
      if (!controller.signal.aborted) setError(errorText(err, t`Could not generate avatar`));
    } finally {
      if (generation.current === controller) {
        generation.current = null;
        setGenerating(false);
      }
    }
  }
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setDraft(value);
          setError("");
          setPhoto(null);
          setOpen(true);
        }}
        className="group relative cursor-pointer rounded-2xl outline-none transition-transform hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t`Customize bot avatar`}
        data-testid="avatar-studio-trigger"
      >
        <BotAvatar color={value} identity={identity} size={size} status={status} />
        <span className="absolute -right-1 -bottom-1 grid size-7 place-items-center rounded-full border-2 border-background bg-secondary text-foreground">
          <Pencil size={13} />
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className="app-no-drag flex max-h-[calc(100dvh-6rem)] w-[420px] max-w-[94vw] flex-col gap-0 overflow-hidden rounded-3xl p-0 sm:max-w-[420px]"
          data-testid="avatar-studio"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
            <Button
              variant="ghost"
              size="icon"
              aria-label={photo ? t`Back` : t`Cancel avatar changes`}
              onClick={() => (photo ? setPhoto(null) : setOpen(false))}
            >
              <ArrowLeft size={20} />
            </Button>
            <DialogTitle className="text-base">
              {photo ? <Trans>Adjust photo</Trans> : <Trans>Avatar</Trans>}
            </DialogTitle>
            {!photo ? (
              <Button
                variant="secondary"
                className="rounded-full"
                disabled={generating}
                onClick={() => {
                  onChange(draft);
                  setOpen(false);
                }}
              >
                <Trans>Done</Trans>
              </Button>
            ) : (
              <span className="w-12" />
            )}
          </div>
          <div className="min-h-0 overflow-y-auto px-5 pt-5 pb-7">
            {photo ? (
              <AvatarPhotoCrop
                file={photo}
                onUse={(color) => {
                  setDraft(color);
                  setPhoto(null);
                }}
                onChoose={() => fileInput.current?.click()}
              />
            ) : (
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mx-auto rounded-full">
                  <TabsTrigger disabled={generating} value="bot" className="rounded-full">
                    <Trans>Presets</Trans>
                  </TabsTrigger>
                  <TabsTrigger disabled={generating} value="generate" className="rounded-full">
                    <Trans>Generate</Trans>
                  </TabsTrigger>
                  <TabsTrigger disabled={generating} value="upload" className="rounded-full">
                    <Trans>Upload</Trans>
                  </TabsTrigger>
                </TabsList>
                <div className="flex justify-center py-6">
                  <BotAvatar color={draft} identity={identity} size={112} />
                </div>
                <TabsContent
                  value="bot"
                  data-testid="avatar-studio-bot-tab"
                  className="rounded-2xl border border-border bg-muted/40 p-4"
                >
                  <div className="grid grid-cols-3 place-items-center gap-3">
                    <button
                      type="button"
                      aria-label="Animated eyes"
                      aria-pressed={
                        !parsed.isImage && !parsed.isBrand && parsed.shapeIndex === undefined
                      }
                      onClick={() => setDraft(currentColor)}
                      className="flex flex-col items-center gap-1 rounded-2xl p-1 hover:bg-accent"
                    >
                      <BotAvatar color={currentColor} identity={identity} size={48} />
                      <span className="text-xs text-muted-foreground">Animated eyes</span>
                    </button>
                    {appMarkUrl ? (
                      <button
                        type="button"
                        aria-label="Andrew head"
                        aria-pressed={Boolean(parsed.isBrand)}
                        onClick={() => setDraft(`${currentColor}::brand`)}
                        className={`grid size-16 place-items-center rounded-2xl border-2 ${parsed.isBrand ? "border-foreground" : "border-transparent"}`}
                      >
                        <BotAvatar color={`${currentColor}::brand`} size={48} />
                      </button>
                    ) : null}
                    {Array.from({ length: 8 }, (_, i) => (
                      <BotShapePreview
                        key={i}
                        size={64}
                        shapeIndex={i}
                        color={currentColor}
                        selected={!parsed.isImage && parsed.shapeIndex === i}
                        onClick={() => setDraft(`${currentColor}::shape_${i}`)}
                      />
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap justify-center gap-4 border-t border-border pt-5">
                    {BOT_AVATAR_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        aria-label={t`Color ${color}`}
                        aria-pressed={
                          !parsed.isImage && currentColor.toLowerCase() === color.toLowerCase()
                        }
                        onClick={() =>
                          setDraft(
                            parsed.isBrand
                              ? `${color}::brand`
                              : parsed.shapeIndex === undefined
                                ? color
                                : `${color}::shape_${parsed.shapeIndex}`,
                          )
                        }
                        className={`size-8 rounded-full border border-border outline-offset-4 ${!parsed.isImage && currentColor.toLowerCase() === color.toLowerCase() ? "outline-2 outline-foreground" : ""}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    className="mt-5 w-full border-t border-border pt-4"
                    data-testid="avatar-studio-default"
                    aria-pressed={isDefault}
                    onClick={() => setDraft(defaultBotAvatarValue(draft))}
                  >
                    <Trans>Reset to default</Trans>
                  </Button>
                </TabsContent>
                <TabsContent value="generate" className="space-y-4">
                  <Textarea
                    aria-label={t`Describe your avatar`}
                    placeholder={t`Describe your avatar…`}
                    value={prompt}
                    maxLength={2000}
                    rows={4}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="rounded-2xl bg-muted/40 p-4"
                  />
                  {available === false ? (
                    <p className="text-sm text-muted-foreground">
                      <Trans>Connect image generation to create an avatar.</Trans>
                    </p>
                  ) : null}
                  <Button
                    className="w-full rounded-full"
                    disabled={!available || !prompt.trim() || generating}
                    onClick={() => void generate()}
                  >
                    <Sparkles size={16} />
                    {generating ? <Trans>Generating…</Trans> : <Trans>Generate</Trans>}
                  </Button>
                </TabsContent>
                <TabsContent value="upload">
                  <button
                    type="button"
                    aria-label={t`Image upload area`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files[0];
                      if (f) chooseFile(f);
                    }}
                    onPaste={(e) => {
                      const f = [...e.clipboardData.items]
                        .find((item) => item.type.startsWith("image/"))
                        ?.getAsFile();
                      if (f) chooseFile(f);
                    }}
                    onClick={() => fileInput.current?.click()}
                    className="flex w-full flex-col items-center gap-3 rounded-2xl border border-border bg-muted/40 p-7"
                  >
                    <ImagePlus size={26} />
                    <span className="text-sm">
                      <Trans>Choose photo</Trans>
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <Trans>Or drag, drop, or paste an image</Trans>
                    </span>
                  </button>
                  <p className="mt-3 text-xs text-muted-foreground">
                    <Trans>Images up to 25 MB. Crop it next.</Trans>
                  </p>
                </TabsContent>
              </Tabs>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) chooseFile(file);
                e.target.value = "";
              }}
            />
            {error ? (
              <p role="alert" className="mt-4 text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
